import React, { useState, useMemo, useCallback, useRef, Suspense } from 'react';
import { generateId } from './utils/uuid';
import storage, { isRecentWrite, flushPendingPushes } from './services/storage';
import { contactService }      from './services/contactService';
import { taskService }         from './services/taskService';
import { meetingService }      from './services/meetingService';
import { pipelineService }     from './services/pipelineService';
import { activityService, latestActivityDateDe } from './services/activityService';
import { buildOfflineSnapshot } from './services/offlineSnapshotService';
import { getTableDensity } from './services/tableDensity';
import FloatingNote from './components/FloatingNote';
import FloatingEmail from './components/FloatingEmail';
import FloatingTeamFeed from './components/FloatingTeamFeed';
import { subscribeTeamActivities } from './services/leadActivitiesCloud';
import { auditService }        from './services/auditService';
import { trashService }        from './services/trashService';
import { importHistoryService } from './services/importHistoryService';
import { notificationService } from './services/notificationService';
import { automationService }   from './services/automationService';
import TopNav         from './components/TopNav';
import CommandPalette from './components/CommandPalette';
import FloatingMeeting from './components/FloatingMeeting';
import FloatingTask    from './components/FloatingTask';
import RegionalSuggestionModal, { REGION_PENDING_KEY } from './components/RegionalSuggestionModal';
import Sidebar   from './components/Sidebar';
import LeadPanel  from './components/LeadPanel';
import TaskModal  from './components/TaskModal';
// All views are lazy-loaded — each becomes its own JS chunk, keeping the main bundle small
const ContactsView  = React.lazy(() => import('./views/ContactsView'));
const DashboardView = React.lazy(() => import('./views/DashboardView'));
const TemplatesView = React.lazy(() => import('./views/TemplatesView'));
const ImportView    = React.lazy(() => import('./views/ImportView'));   // includes xlsx on demand
const CallsView     = React.lazy(() => import('./views/CallsView'));
const TasksView     = React.lazy(() => import('./views/TasksView'));
const PipelineView  = React.lazy(() => import('./views/PipelineView'));
const MapView       = React.lazy(() => import('./views/MapView'));      // includes Leaflet on demand
const TerminView    = React.lazy(() => import('./views/TerminView'));
const AngebotsView  = React.lazy(() => import('./views/AngebotsView'));
const AutomationView = React.lazy(() => import('./views/AutomationView'));
const AnalyticsView  = React.lazy(() => import('./views/AnalyticsView'));
const InsightsView   = React.lazy(() => import('./views/InsightsView'));
const TrashView      = React.lazy(() => import('./views/TrashView'));
const CompanyView    = React.lazy(() => import('./views/CompanyView'));
const ReportsView    = React.lazy(() => import('./views/ReportsView'));
import NotificationCenter from './components/NotificationCenter';
const InboxPanel = lazyWithSuspense(() => import('./components/InboxPanel'));
import messageService from './services/messageService';
import { LEADS, LISTS, DEFAULT_STATUS_LIST, buildStatusMeta, DEMO_LEADS, DEMO_TASKS, DEMO_MEETINGS, getDemoData } from './data/leads';
const StatusSettingsModal = lazyWithSuspense(() => import('./components/StatusSettingsModal'));
import NewContactModal     from './components/NewContactModal';
import { loadBestandskunden, loadAutoWiedervorlageSettings } from './utils/appSettings';
const SettingsModal = lazyWithSuspense(() => import('./components/SettingsModal'));
import SyncStatusBar from './components/SyncStatusBar';
import { loadBranding, applyBranding } from './utils/branding';
const BrandingModal = lazyWithSuspense(() => import('./components/BrandingModal'));
const CalendarSyncModal = lazyWithSuspense(() => import('./components/CalendarSyncModal'));
const AppointmentEmailModal = lazyWithSuspense(() => import('./components/AppointmentEmailModal'));
import { handleOutlookCallback, isOutlookConnected, getOutlookUser, getOutlookCalendarEvents } from './utils/msGraph';
import {
  getStorageKey, getCurrentSessionRole, ForcePasswordChangeScreen, LoginScreen, DemoBranchScreen,
  clearMustChangePassword, hydrateUserData,
} from './auth';
import { AuthProvider, useAuth, registerCrmDataLoader } from './context/AuthContext';
import { loadUserDataIntoState } from './utils/crmLoadUserData';
import { mergeSessionArrays } from './services/userDataSync';
import { migratePrivateBucketsToTeam, ensureBuiltinLists } from './utils/teamCrmStore';
import { pullTeamCrmIntoLocal, pushDatasetToTeamCloud, subscribeTeamCrmSnapshot } from './services/teamCrmCloud';
import { useCrmContactsScope } from './hooks/useCrmData';
import { supabase, isConfigured } from './services/supabase';
import { runReminderNotificationTick } from './services/reminderWebNotifications';
import { loadSavedSessions, CallSessionOverlay, CallSessionsModal } from './components/CallSession';
const NextLeadQueueModal = lazyWithSuspense(() => import('./components/NextLeadQueueModal'));
import { buildHandoffNotes, addWorkdays } from './utils/activitySummary';
import MobileLayout from './views/MobileLayout';
import { useIsMobile, getViewMode, setViewMode, detectMobileDevice } from './utils/useIsMobile';
import { subscribeToUserData, subscribePresence } from './services/realtimeService';

const MOTION_PRESET = 'snappy'; // 'smooth' | 'snappy' | 'ultra-fast'

/* Lazy-Modal mit eingebautem Suspense — Render-Stellen bleiben unverändert */
function lazyWithSuspense(loader) {
  const L = React.lazy(loader);
  return function LazyWrapped(props) {
    return <Suspense fallback={null}><L {...props} /></Suspense>;
  };
}

/* ── Automation helpers ────────────────────────────────────────────────────── */
function fillTemplate(str, lead) {
  return (str || '').replace(/\{firma\}/gi, lead.company || '').replace(/\{name\}/gi, lead.name || '');
}

function runAutomations({ rules, leads, tasks, onCreateTask, onAddNotification, onUpdateLead, currentUserId }) {
  if (!rules || rules.length === 0) return;
  const today = new Date().toDateString();
  const firedKey = currentUserId ? `crm_auto_fired_u${currentUserId}` : 'crm_auto_fired';
  const fired = storage.get(firedKey, {});

  rules.filter(r => r.active).forEach(rule => {
    leads.forEach(lead => {
      const fk = `${rule.id}_${lead.id}`;
      if (fired[fk] === today) return;

      let triggered = false;
      const t = rule.trigger;

      if (t.type === 'no_contact_days') {
        const days = t.days || 3;
        const lastContact = lead.lastContact ? new Date(lead.lastContact) : (lead.activity ? parseActivityDate(lead.activity) : null);
        if (lastContact) {
          const diffDays = Math.floor((Date.now() - lastContact.getTime()) / 86400000);
          if (diffDays >= days) triggered = true;
        }
      }
      if (t.type === 'status_change' && t.toStatus) {
        if (lead.status === t.toStatus && lead._statusChangedAt) {
          const diffDays = Math.floor((Date.now() - new Date(lead._statusChangedAt).getTime()) / 86400000);
          if (diffDays === 0) triggered = true;
        }
      }
      if (t.type === 'lead_created' && lead.createdAt) {
        const diffDays = Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / 86400000);
        if (diffDays === 0) triggered = true;
      }
      if (t.type === 'wiedervorlage_due' && lead.wiedervorlage) {
        const wDate = new Date(lead.wiedervorlage);
        wDate.setHours(0, 0, 0, 0);
        const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
        if (wDate <= todayD) triggered = true;
      }
      if (t.type === 'task_overdue') {
        const overdue = tasks.some(tk => !tk.done && tk.leadId === lead.id && tk.dueDatetime && new Date(tk.dueDatetime) < new Date());
        if (overdue) triggered = true;
      }

      if (!triggered) return;
      fired[fk] = today;

      const a = rule.action;
      if (a.type === 'create_task') {
        const dueDate = new Date(Date.now() + (a.taskDueDays || 0) * 86400000);
        onCreateTask({
          id: generateId(),
          title: fillTemplate(a.taskTitle || 'Follow-up', lead),
          leadId: lead.id,
          leadName: lead.name,
          done: false,
          dueDatetime: dueDate.toISOString(),
          source: 'automation',
          ruleId: rule.id,
          createdAt: new Date().toISOString(),
        });
      }
      if (a.type === 'add_notification') {
        onAddNotification({
          id: generateId(),
          type: 'automation',
          title: fillTemplate(a.notifTitle || 'Automation ausgelöst', lead),
          message: `Lead: ${lead.name}`,
          leadId: lead.id,
          leadName: lead.name,
          read: false,
          createdAt: new Date().toISOString(),
        });
      }
      if (a.type === 'change_status' && a.newStatus) {
        onUpdateLead(lead.id, { status: a.newStatus, _statusChangedAt: new Date().toISOString() });
      }
    });
  });

  storage.set(firedKey, fired);
}

function parseActivityDate(str) {
  if (!str) return null;
  const parts = str.split('.');
  if (parts.length === 3) return new Date(+parts[2], +parts[1] - 1, +parts[0]);
  return null;
}

const MOTION_PRESETS = ['smooth', 'snappy', 'ultra-fast'];

/* (storage helpers moved to src/services/ — use the service layer, not localStorage directly) */

class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#060a12', flexDirection: 'column', gap: 16, padding: 32 }}>
          <div style={{ color: '#ef4444', fontSize: 16, fontWeight: 700 }}>App-Fehler (bitte Screenshot schicken)</div>
          <pre style={{ color: '#fca5a5', fontSize: 12, maxWidth: 600, whiteSpace: 'pre-wrap', background: '#1a0000', padding: 16, borderRadius: 8 }}>
            {this.state.error.toString()}{'\n'}{this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <CrmApplication />
      </AuthProvider>
    </AppErrorBoundary>
  );
}

/* ─────────────────────────────────────────────
   CRM-Hauptanwendung (Auth via AuthProvider)
───────────────────────────────────────────── */
function CrmApplication() {
  const isMobile = useIsMobile();
  const {
    authUsers,
    setAuthUsers,
    authSession,
    currentUser,
    currentPermissions,
    loginError,
    appReady,
    isHydrated,
    handleLogin,
    handleLogout,
    handleUsersChange,
  } = useAuth();
  const [motionPreset, setMotionPreset] = useState(() => storage.get('crm_motion_preset', 'snappy'));
  const [confetti, setConfetti] = useState(null); // { reason: 'won'|'appt' }
  const confettiTimer = React.useRef(null);
  function fireConfetti(reason) {
    if (confettiTimer.current) clearTimeout(confettiTimer.current);
    setConfetti({ reason });
    confettiTimer.current = setTimeout(() => setConfetti(null), 4000);
  }
  const [theme, setTheme] = useState(() => {
    const saved = storage.get('theme', null);
    if (saved) return saved;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    storage.set('theme', theme);
  }, [theme]);
  React.useEffect(() => {
    const preset = MOTION_PRESETS.includes(motionPreset) ? motionPreset : 'snappy';
    storage.set('crm_motion_preset', preset);
  }, [motionPreset]);
  React.useEffect(() => {
    const urlPreset = new URL(window.location.href).searchParams.get('motion');
    if (urlPreset && MOTION_PRESETS.includes(urlPreset)) {
      setMotionPreset(urlPreset);
    }
  }, []);
  React.useEffect(() => {
    document.documentElement.setAttribute('data-motion', MOTION_PRESET);
  }, []);
  /* Handle Microsoft OAuth callback (?code=...) */
  React.useEffect(() => {
    const url    = new URL(window.location.href);
    const code   = url.searchParams.get('code');
    const msErr  = url.searchParams.get('error');
    if (code) {
      window.history.replaceState(null, '', window.location.pathname);
      handleOutlookCallback(code)
        .then(userInfo => {
          setOutlookConnected(true);
          setOutlookUser(userInfo);
          setOutlookAuthMsg('✓ Outlook erfolgreich verbunden!' + (userInfo?.displayName ? ` Hallo, ${userInfo.displayName}!` : ''));
          setTimeout(() => setOutlookAuthMsg(''), 5000);
        })
        .catch(e => {
          setOutlookAuthMsg('Outlook-Verbindung fehlgeschlagen: ' + e.message);
          setTimeout(() => setOutlookAuthMsg(''), 6000);
        });
    } else if (msErr) {
      window.history.replaceState(null, '', window.location.pathname);
      setOutlookAuthMsg('Outlook-Login abgebrochen.');
      setTimeout(() => setOutlookAuthMsg(''), 4000);
    }
  }, []); // eslint-disable-line

  /* Open lead from URL hash (e.g. #lead-5 set by MapView) */
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#lead-')) {
      const id = parseInt(hash.slice(6), 10);
      try {
        const allLeads = contactService.getAll(null);
        const lead = allLeads.find(l => l.id === id);
        if (lead) {
          setActiveSection('contacts');
          setSelectedLead(lead);
        }
      } catch {}
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []); // eslint-disable-line

  const [activeSection, setActiveSection] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('section') || 'dashboard';
  });
  const [activeList,    setActiveList]    = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('list') || 'all';
  });
  const [search,        setSearch]        = useState('');
  const [selectedLead,  setSelectedLead]  = useState(null);
  const [statusFilter,  setStatusFilter]  = useState(null);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [regionalSession, setRegionalSession] = useState(null); // { ids, label, leads }
  const [taskModal,     setTaskModal]     = useState(false);
  const [leads, setLeads] = useState(() => {
    const key = getStorageKey('crm_leads');
    const uid = key === 'crm_leads' ? null : key.replace('crm_leads_u', '');
    const stored = contactService.getAll(uid || null);
    if (stored.length > 0) return stored;
    if (!uid) return [];
    return getCurrentSessionRole() === 'demo' ? [...DEMO_LEADS] : [];
  });
  const [lists, setLists] = useState(() => {
    const listsKey = getStorageKey('crm_lists');
    const stored = storage.get(listsKey, null);
    let base = [];
    if (stored && Array.isArray(stored) && stored.length > 0) {
      base = stored;
    }
    // Recover lists from leads that weren't saved
    const leadsKey = getStorageKey('crm_leads');
    const uid = leadsKey === 'crm_leads' ? null : leadsKey.replace('crm_leads_', '').replace('u', '');
    const savedLeads = (() => { try { const r = localStorage.getItem(leadsKey); return r ? JSON.parse(r) : []; } catch { return []; } })();
    const knownIds = new Set(base.map(l => l.id));
    const orphans = [...new Set(savedLeads.map(l => l.list).filter(id => id && !knownIds.has(id)))];
    return [...base, ...orphans.map(id => ({ id, label: id.startsWith('import_') ? 'Importierte Liste' : id }))];
  });
  const [lastLead,       setLastLead]       = useState(null);
  const [importPopup,    setImportPopup]    = useState(null);
  const [tasks, setTasks] = useState(() => {
    const key = getStorageKey('crm_tasks');
    const uid = key === 'crm_tasks' ? null : key.replace('crm_tasks_u', '');
    const stored = taskService.getAll(uid || null);
    if (stored.length > 0) return stored;
    if (!uid) return [];
    return getCurrentSessionRole() === 'demo' ? [...DEMO_TASKS] : [];
  });
  const [meetings, setMeetings] = useState(() => {
    const key = getStorageKey('crm_meetings');
    const uid = key === 'crm_meetings' ? null : key.replace('crm_meetings_u', '');
    const stored = meetingService.getAll(uid || null);
    if (stored.length > 0) return stored;
    if (!uid) return [];
    return getCurrentSessionRole() === 'demo' ? [...DEMO_MEETINGS] : [];
  });
  const [pipelineStages, setPipelineStages] = useState(() => {
    const key = getStorageKey('crm_pipeline_stages');
    const uid = key === 'crm_pipeline_stages' ? null : key.replace('crm_pipeline_stages_u', '');
    return pipelineService.get(uid || null);
  });
  const [showMeeting,    setShowMeeting]    = useState(false);
  const [dueNotif,       setDueNotif]       = useState(null);
  const [statusList, setStatusList] = useState(() => {
    const saved = storage.get('crm_statusList', null);
    if (saved) {
      const ids = new Set(saved.map(s => s.id));
      return [...saved, ...DEFAULT_STATUS_LIST.filter(s => !ids.has(s.id))];
    }
    return DEFAULT_STATUS_LIST.map(s => ({ ...s }));
  });
  const [statusMeta, setStatusMeta] = useState(() => buildStatusMeta(statusList));
  const [showStatusSettings, setShowStatusSettings] = useState(false);
  const [showNewContact,     setShowNewContact]     = useState(false);
  const [showSettings,       setShowSettings]       = useState(false);
  const [showBranding,       setShowBranding]       = useState(false);
  // Apply saved branding on mount
  useState(() => { applyBranding(loadBranding()); });
  const [reconnectToast,      setReconnectToast]      = useState(false);
  const [showCreateTask,      setShowCreateTask]      = useState(false);
  const [quickTaskLead,       setQuickTaskLead]       = useState(null);
  const [quickNoteLead,       setQuickNoteLead]       = useState(null);
  const [quickEmailLead,      setQuickEmailLead]      = useState(null);
  const [tableDensity,        setTableDensity]        = useState(() => getTableDensity());
  const [callSession,         setCallSession]         = useState(() => {
    try {
      const raw = storage.get(REGION_PENDING_KEY, null);
      if (raw) {
        storage.remove(REGION_PENDING_KEY);
        const data = raw;
        if (Array.isArray(data.leads) && data.leads.length > 0) {
          return {
            leads: data.leads,
            sessionName: data.sessionName || `Region ${data.sourceCity || ''} – ${data.radiusKm || 30} km`,
          };
        }
      }
    } catch {}
    return null;
  });
  const [showSessionsModal,   setShowSessionsModal]   = useState(false);
  const [nextLeadQueue,       setNextLeadQueue]       = useState(null);
  const [bestandskunden, setBestandskunden] = useState(loadBestandskunden);
  const [recentLeads,        setRecentLeads]        = useState(() => storage.get('crm_recent', []));
  const [emailDraftMeeting,  setEmailDraftMeeting]  = useState(null);
  const [showCalendarSync,   setShowCalendarSync]   = useState(false);
  const [outlookConnected,   setOutlookConnected]   = useState(isOutlookConnected);
  const [outlookUser,        setOutlookUser]        = useState(getOutlookUser);
  const [outlookAuthMsg,     setOutlookAuthMsg]     = useState('');
  const searchInputRef = React.useRef(null);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const historyReadyRef = React.useRef(false);
  const restoringHistoryRef = React.useRef(false);
  const leadRestoredRef = React.useRef(false);
  /* ── New feature state ─────────────────────────────────────────────────── */
  const [automations,   setAutomations]   = useState(() => automationService.getAll(authSession?.userId));
  const [auditLog,      setAuditLog]      = useState(() => auditService.getAll(authSession?.userId));
  const [trash,         setTrash]         = useState(() => trashService.getAll(authSession?.userId));
  const [notifications, setNotifications] = useState(() => notificationService.getAll(authSession?.userId));
  const [inboxMessages, setInboxMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]); // Realtime presence
  const [showInbox,     setShowInbox]     = useState(false);
  const [appErrors,     setAppErrors]     = useState([]);
  const [listAssignVersion, setListAssignVersion] = useState(0);

  function pushError(code, message, detail = '') {
    const id = Date.now();
    const ts = new Date().toLocaleTimeString('de-DE');
    setAppErrors(prev => [...prev.slice(-2), { id, code, message, detail, ts }]);
    setTimeout(() => setAppErrors(prev => prev.filter(e => e.id !== id)), 10000);
  }

  React.useEffect(() => {
    function onCrmError(e) {
      pushError(e.detail.code, e.detail.message, e.detail.detail);
    }
    window.addEventListener('crm-error', onCrmError);
    return () => window.removeEventListener('crm-error', onCrmError);
  }, []); // eslint-disable-line

  /* ── Load messages from Supabase + realtime subscription ────────────────── */
  React.useEffect(() => {
    if (!currentUser?.id) { setInboxMessages([]); return; }
    let cancelled = false;

    messageService.getAll(currentUser.id)
      .then(msgs => { if (!cancelled) setInboxMessages(msgs); })
      .catch(err => pushError('MSG_LOAD_FAIL', 'Nachrichten konnten nicht geladen werden.', err?.message));

    const unsub = messageService.subscribe(
      currentUser.id,
      (newMsg) => {
        if (cancelled) return;
        setInboxMessages(prev =>
          prev.some(m => m.id === newMsg.id)
            ? prev.map(m => m.id === newMsg.id ? newMsg : m)
            : [newMsg, ...prev]
        );
      },
      (updatedMsg) => {
        if (cancelled) return;
        setInboxMessages(prev => prev.map(m => m.id === updatedMsg.id ? updatedMsg : m));
      }
    );

    return () => { cancelled = true; unsub(); };
  }, [currentUser?.id]); // eslint-disable-line

  /* Team-Aktivitäten: Realtime-Sync (Tabelle crm_team_activities) */
  React.useEffect(() => {
    if (!isConfigured || !currentUser?.id) return () => {};
    const unsub = subscribeTeamActivities();
    return () => unsub();
  }, [currentUser?.id]); // eslint-disable-line

  const reloadTeamCrmState = React.useCallback(() => {
    migratePrivateBucketsToTeam();
    const nextLeads = contactService.getAll();
    setLeads(nextLeads);
    const storedLists = storage.get('crm_lists', null);
    setLists(ensureBuiltinLists(storedLists, nextLeads));
    setTasks(taskService.getAll());
    setMeetings(meetingService.getAll());
  }, []);

  /* Restore selected lead from URL on first load */
  React.useEffect(() => {
    if (leadRestoredRef.current || leads.length === 0) return;
    leadRestoredRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const leadId = params.get('lead');
    if (!leadId) return;
    const lead = leads.find(l => String(l.id) === leadId);
    if (lead) {
      setSelectedLead(lead);
      setListVisible(false);
    }
  }, [leads]);

  /* Team-Leads/Listen: Cloud + Migration (alle Nutzer sehen alle Leads) */
  React.useEffect(() => {
    if (!currentUser?.id) return () => {};
    migratePrivateBucketsToTeam();
    pullTeamCrmIntoLocal()
      .then(() => reloadTeamCrmState())
      .catch(() => {});
    const unsub = subscribeTeamCrmSnapshot(() => reloadTeamCrmState());
    const onChanged = () => reloadTeamCrmState();
    window.addEventListener('crm-team-data-changed', onChanged);
    return () => {
      unsub();
      window.removeEventListener('crm-team-data-changed', onChanged);
    };
  }, [currentUser?.id, reloadTeamCrmState]);

  // ── Supabase Realtime: sync leads when another user saves ──────────────
  React.useEffect(() => {
    if (!currentUser?.id) return;
    const unsubData = subscribeToUserData(currentUser.id, ({ key, userId, value, updatedAt, isSelfUpdate }) => {
      if (isSelfUpdate && /^crm_(leads|lists|trash)/.test(key)) {
        // Only reload for admin-pushed SQL resets (far-future timestamp like 2099-01-01).
        // Normal saves have updated_at = NOW() so they never trigger this.
        const ts = updatedAt ? new Date(updatedAt).getTime() : 0;
        const oneYearFromNow = Date.now() + 365 * 24 * 60 * 60 * 1000;
        if (ts > oneYearFromNow) {
          try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
          window.location.reload();
        }
        return;
      }
      // Sync call sessions from another device (same user only — never from teammates!)
      // Merge pro Session-ID statt Überschreiben, damit parallele Geräte sich nicht clobbern.
      if (key === 'crm_call_sessions' && isSelfUpdate && !isRecentWrite(key)) {
        try {
          const local = storage.get('crm_call_sessions', []);
          const merged = mergeSessionArrays(local, value);
          localStorage.setItem(key, JSON.stringify(merged));
        } catch {}
        window.dispatchEvent(new CustomEvent('crm-sessions-updated'));
      }
    });
    const unsubPresence = subscribePresence(currentUser, (onlineList) => {
      if (Array.isArray(onlineList)) setOnlineUsers(onlineList);
    });
    return () => { unsubData(); unsubPresence(); };
  }, [currentUser?.id]); // eslint-disable-line

  // ── Re-Sync beim Zurückkehren in die App (Tab-Fokus, App-Wechsel, Reconnect) ──
  // Mobil stirbt der Realtime-Websocket im Hintergrund; verpasste Updates werden
  // hier nachgeholt: erst eigene offene Pushes raus, dann Cloud → lokal mergen.
  const resyncBusyRef = React.useRef(false);
  const lastResyncRef = React.useRef(0);
  React.useEffect(() => {
    if (!currentUser?.id || !isConfigured) return;
    const userId = currentUser.id;

    async function resync(force = false) {
      if (document.visibilityState === 'hidden' && !force) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        window.dispatchEvent(new CustomEvent('crm-synced', { detail: { ok: false } }));
        return;
      }
      const now = Date.now();
      if (resyncBusyRef.current || (!force && now - lastResyncRef.current < 15000)) return;
      resyncBusyRef.current = true;
      lastResyncRef.current = now;
      let ok = false;
      try {
        await flushPendingPushes();
        await hydrateUserData(userId);
        window.dispatchEvent(new CustomEvent('crm-sessions-updated'));
        await pullTeamCrmIntoLocal().catch(() => {});
        reloadTeamCrmState();
        ok = true;
        storage.setLocal('crm_last_sync_at', new Date().toISOString());
      } catch { /* nächster Fokus versucht es erneut */ }
      finally {
        resyncBusyRef.current = false;
        window.dispatchEvent(new CustomEvent('crm-synced', { detail: { ok } }));
      }
    }

    const onVisible = () => { if (document.visibilityState === 'visible') resync(); };
    const onOnline  = () => resync();
    const onRequest = () => resync(true); // Pull-to-Refresh u. Ä.
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('crm-request-resync', onRequest);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('crm-request-resync', onRequest);
    };
  }, [currentUser?.id, reloadTeamCrmState]); // eslint-disable-line

  const teamPushTimerRef = React.useRef(null);
  const tasksRef = React.useRef(tasks);
  const leadsRef = React.useRef(leads);
  const selectedLeadRef = useRef(selectedLead);
  React.useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  React.useEffect(() => { leadsRef.current = leads; }, [leads]);
  React.useEffect(() => { selectedLeadRef.current = selectedLead; }, [selectedLead]);
  React.useEffect(() => {
    if (!currentUser?.id) return () => {};
    clearTimeout(teamPushTimerRef.current);
    teamPushTimerRef.current = setTimeout(() => {
      pushDatasetToTeamCloud('leads', leads);
      pushDatasetToTeamCloud('lists', lists);
      pushDatasetToTeamCloud('tasks', tasks);
      pushDatasetToTeamCloud('meetings', meetings);
    }, 8000);
    return () => clearTimeout(teamPushTimerRef.current);
  }, [leads, lists, tasks, meetings, currentUser?.id]);

  function handleSaveStatusList(newList) {
    const copy = newList.map(s => ({ ...s }));
    storage.set('crm_statusList', copy);
    setStatusList(copy);
    setStatusMeta(buildStatusMeta(copy));
  }

  /* serviceUserId: null = admin (global key), id = non-admin (scoped key) */
  const serviceUserId = useMemo(
    () => (!currentUser?.role || currentUser.role === 'admin') ? null : currentUser.id,
    [currentUser]
  );

  React.useEffect(() => {
    if (!currentUser || !isHydrated) return;
    storage.set('crm_lists', lists);
  }, [lists, currentUser?.id, isHydrated]); // eslint-disable-line

  React.useEffect(() => {
    if (!currentUser || !isHydrated) return;
    contactService.saveAll(serviceUserId, leads);
  }, [leads, currentUser?.id, isHydrated]); // eslint-disable-line

  React.useEffect(() => {
    if (!currentUser || !isHydrated) return;
    taskService.saveAll(serviceUserId, tasks);
  }, [tasks, currentUser?.id, isHydrated]); // eslint-disable-line

  React.useEffect(() => {
    if (!currentUser || !isHydrated) return;
    meetingService.saveAll(serviceUserId, meetings);
  }, [meetings, currentUser?.id, isHydrated]); // eslint-disable-line

  React.useEffect(() => {
    if (!currentUser || !isHydrated) return;
    pipelineService.save(serviceUserId, pipelineStages);
  }, [pipelineStages, currentUser?.id, isHydrated]); // eslint-disable-line

  React.useEffect(() => { automationService.saveAll(currentUser?.id, automations); }, [automations, currentUser?.id]); // eslint-disable-line
  React.useEffect(() => { auditService.saveAll(currentUser?.id, auditLog); }, [auditLog, currentUser?.id]); // eslint-disable-line
  React.useEffect(() => { trashService.saveAll(currentUser?.id, trash); }, [trash, currentUser?.id]); // eslint-disable-line
  React.useEffect(() => { notificationService.saveAll(currentUser?.id, notifications); }, [notifications, currentUser?.id]); // eslint-disable-line

  /* Reload per-user state when user changes */
  React.useEffect(() => {
    if (!currentUser) return;
    setAutomations(automationService.getAll(currentUser.id));
    setAuditLog(auditService.getAll(currentUser.id));
    setTrash(trashService.getAll(currentUser.id));
    setNotifications(notificationService.getAll(currentUser.id));
  }, [currentUser?.id]); // eslint-disable-line

  React.useEffect(() => {
    registerCrmDataLoader((user) => {
      loadUserDataIntoState(user, {
        setLeads,
        setLists,
        setTasks,
        setMeetings,
        setPipelineStages,
        setAutomations,
        setAuditLog,
        setTrash,
        setNotifications,
      });
    });
    return () => registerCrmDataLoader(null);
  }, []); // eslint-disable-line

  async function handleSendMessage(message, toUserIds) {
    try {
      let enriched = message;
      // For list assignments, embed the actual leads so recipients don't need admin's localStorage
      if (message.type === 'list_assignment' && message.payload?.listId) {
        const listLeads = leads.filter(l => l.list === message.payload.listId);
        enriched = { ...message, payload: { ...message.payload, leads: listLeads } };
      }
      await messageService.send(toUserIds, enriched);
    } catch (err) {
      pushError('MSG_SEND_FAIL', 'Nachricht konnte nicht gesendet werden.', err?.message || String(err));
      throw err;
    }
  }

  async function handleMarkMessageRead(messageId) {
    if (!currentUser?.id) return;
    setInboxMessages(prev => prev.map(m => m.id === messageId ? { ...m, read: true } : m));
    await messageService.markRead(messageId, currentUser.id).catch(() => {});
  }

  async function handleAcceptList(msg) {
    try {
      const { listId, listLabel, leads: payloadLeads } = msg.payload;
      const assignedLeads = Array.isArray(payloadLeads) ? payloadLeads : [];
      const adminList = { id: listId, label: listLabel };
      if (!lists.some(l => l.id === listId)) setLists(prev => [...prev, adminList]);
      if (assignedLeads.length > 0) {
        const existingIds = new Set(leads.map(l => l.id));
        const newLeads = assignedLeads.filter(l => !existingIds.has(l.id));
        if (newLeads.length > 0) setLeads(prev => [...prev, ...newLeads]);
      }
      if (assignedLeads.length === 0) {
        pushError('LIST_EMPTY', `Liste "${listLabel}" hat keine Leads — der Admin hat sie möglicherweise noch nicht befüllt.`, `listId=${listId}`);
      }
      await messageService.updateStatus(msg.id, 'accepted');
      setInboxMessages(prev => prev.map(m => m.id === msg.id ? { ...m, status: 'accepted', read: true } : m));
    } catch (err) {
      pushError('LIST_ACCEPT_FAIL', 'Liste konnte nicht angenommen werden.', err?.message || String(err));
    }
  }

  async function handleDeclineList(msg) {
    try {
      await messageService.updateStatus(msg.id, 'declined');
      setInboxMessages(prev => prev.map(m => m.id === msg.id ? { ...m, status: 'declined', read: true } : m));
    } catch (err) {
      pushError('LIST_DECLINE_FAIL', 'Liste konnte nicht abgelehnt werden.', err?.message || String(err));
    }
  }

  /* Run automations on mount and every 60 min */
  React.useEffect(() => {
    if (!currentUser) return;
    function run() {
      runAutomations({
        rules: automations,
        leads,
        tasks,
        onCreateTask: t => setTasks(prev => [...prev, t]),
        onAddNotification: n => setNotifications(prev => [n, ...prev]),
        onUpdateLead: (id, ch) => setLeads(prev => prev.map(l => l.id === id ? { ...l, ...ch } : l)),
        currentUserId: currentUser.id,
      });
      /* Check wiedervorlage */
      const today = new Date(); today.setHours(0, 0, 0, 0);
      leads.forEach(lead => {
        if (!lead.wiedervorlage) return;
        const wd = new Date(lead.wiedervorlage); wd.setHours(0, 0, 0, 0);
        if (wd <= today) {
          setNotifications(prev => {
            const alreadyExists = prev.some(n => n.type === 'wiedervorlage' && n.leadId === lead.id && n.createdAt?.startsWith(today.toISOString().slice(0, 10)));
            if (alreadyExists) return prev;
            return [{ id: generateId(), type: 'wiedervorlage', title: `Wiedervorlage fällig: ${lead.name}`, message: lead.company || '', leadId: lead.id, leadName: lead.name, read: false, createdAt: new Date().toISOString() }, ...prev];
          });
        }
      });
      /* Automatische Wiedervorlage: leads with no contact for N days */
      const autoWvSettings = loadAutoWiedervorlageSettings();
      if (autoWvSettings.enabled) {
        const thresholdDays = autoWvSettings.days || 3;
        const allowedLists = autoWvSettings.lists || [];
        const autoLeads = [];
        leads.forEach(lead => {
          if (lead.wiedervorlage) return;
          if (!lead.activity) return;
          if (allowedLists.length > 0 && !allowedLists.includes(lead.list)) return;
          const parts = lead.activity.split('.');
          if (parts.length !== 3) return;
          const actDate = new Date(+parts[2], +parts[1] - 1, +parts[0]);
          if (isNaN(actDate.getTime())) return;
          const daysSince = (today.getTime() - actDate.getTime()) / (24 * 60 * 60 * 1000);
          if (daysSince < thresholdDays) return;
          autoLeads.push({ lead, daysSince });
        });
        if (autoLeads.length > 0) {
          const autoDate = new Date(today);
          autoDate.setDate(autoDate.getDate() + 1);
          const autoDateStr = autoDate.toISOString().slice(0, 10);
          const todayStr = today.toISOString().slice(0, 10);
          setLeads(prev => prev.map(l => {
            const entry = autoLeads.find(a => a.lead.id === l.id);
            return (entry && !l.wiedervorlage) ? { ...l, wiedervorlage: autoDateStr } : l;
          }));
          autoLeads.forEach(({ lead, daysSince }) => {
            const actAlreadyExists = activityService.getForLead(lead.id)
              .some(a => a.type === 'wiedervorlage' && (a.ts || a.date || '').startsWith(todayStr));
            if (!actAlreadyExists) {
              const wvDate = new Date();
              const monthStr = wvDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
              activityService.addToLead(lead.id, {
                id: generateId(),
                type: 'wiedervorlage',
                title: 'Automatische Wiedervorlage gesetzt',
                note: `Kein Kontakt seit ${Math.floor(daysSince)} Tag${Math.floor(daysSince) !== 1 ? 'en' : ''}`,
                date: new Date().toLocaleDateString('de-DE'),
                month: monthStr,
                ts: new Date().toISOString(),
                by: 'System',
              });
            }
            setNotifications(prev => {
              const alreadyExists = prev.some(n => n.type === 'auto_wiedervorlage' && n.leadId === lead.id && n.createdAt?.startsWith(todayStr));
              if (alreadyExists) return prev;
              return [{ id: generateId(), type: 'auto_wiedervorlage', title: `Automatische Wiedervorlage: ${lead.name}`, message: `Kein Kontakt seit ${Math.floor(daysSince)} Tag${Math.floor(daysSince) !== 1 ? 'en' : ''}`, leadId: lead.id, leadName: lead.name, read: false, createdAt: new Date().toISOString() }, ...prev];
            });
          });
        }
      }
    }
    run();
    const interval = setInterval(run, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser?.id, automations.length]); // eslint-disable-line

  const handleBulkDelete = useCallback((ids) => {
    if (!ids?.length) return;
    setLeads(prev => prev.filter(l => !ids.includes(l.id)));
    setSelectedLead(prev => prev && ids.includes(prev.id) ? null : prev);
    setSelectedIds(new Set());
  }, []);

  const handleBulkUpdate = useCallback((ids, changes) => {
    const nowIso = new Date().toISOString();
    setLeads(prev => prev.map(l => {
      if (!ids.includes(l.id)) return l;
      const next = { ...l, ...changes };
      if (Object.prototype.hasOwnProperty.call(changes, 'status') && changes.status !== l.status) {
        next._statusChangedAt = nowIso;
      }
      return next;
    }));
    setSelectedLead(prev => {
      if (!prev || !ids.includes(prev.id)) return prev;
      const next = { ...prev, ...changes };
      if (Object.prototype.hasOwnProperty.call(changes, 'status') && changes.status !== prev.status) {
        next._statusChangedAt = nowIso;
      }
      return next;
    });
    setSelectedIds(new Set());
  }, []);

  function handleCreateContact(newLead) {
    const ts = new Date().toISOString();
    const contact = {
      ...newLead,
      id: generateId(),
      ownerId: currentUser?.id || null,
      companyId: null,
      tags: newLead.tags || [],
      createdAt: ts,
      updatedAt: ts,
      _statusChangedAt: ts,
    };
    setLeads(prev => [contact, ...prev]);
  }

  function handleMergeIntoLead(targetId, patch) {
    const existing = leads.find(l => l.id === targetId);
    if (!existing) return;
    const mergeKeys = ['name', 'firstName', 'lastName', 'company', 'email', 'tel', 'city', 'domain', 'branch'];
    const updates = {};
    mergeKeys.forEach((k) => {
      if (!Object.prototype.hasOwnProperty.call(patch, k)) return;
      const v = patch[k];
      if (v == null || v === '' || v === '–') return;
      const cur = existing[k];
      if (cur == null || cur === '' || cur === '–') updates[k] = v;
    });
    if (Object.keys(updates).length) handleUpdateLead(targetId, updates, Object.keys(updates));
  }

  function handleDeleteLead(leadId) {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    const trashEntry = { ...lead, deletedAt: new Date().toISOString(), deletedBy: { id: currentUser?.id, name: currentUser?.name } };
    setTrash(prev => [trashEntry, ...prev]);
    setLeads(prev => prev.filter(l => l.id !== leadId));
    if (selectedLead?.id === leadId) { setSelectedLead(null); setListVisible(true); }
  }

  function handleRestoreFromTrash(leadId) {
    const entry = trash.find(t => t.id === leadId);
    if (!entry) return;
    const { deletedAt, deletedBy, ...lead } = entry;
    setLeads(prev => [lead, ...prev]);
    setTrash(prev => prev.filter(t => t.id !== leadId));
  }

  function handlePermanentDelete(leadId) {
    setTrash(prev => prev.filter(t => t.id !== leadId));
  }

  function addAuditEntry(leadId, field, oldVal, newVal) {
    if (oldVal === newVal) return;
    setAuditLog(prev => [{
      id: generateId(),
      leadId,
      field,
      oldValue: oldVal,
      newValue: newVal,
      userId: currentUser?.id,
      userName: currentUser?.name || currentUser?.email || 'Unbekannt',
      timestamp: new Date().toISOString(),
    }, ...prev]);
  }

  const counts = useMemo(() => {
    const c = { all: leads.length };
    lists.forEach(l => { if (l.id !== 'all') c[l.id] = leads.filter(lead => lead.list === l.id).length; });
    c.top10 = Math.min(10, leads.length);
    return c;
  }, [leads, lists]);

  const {
    leadsForContactsView,
    listsForContactsView,
    countsForContactsView,
  } = useCrmContactsScope(leads, lists, currentUser, listAssignVersion);

  const [listVisible, setListVisible] = useState(true);

  const selectedLeads = leads.filter(l => selectedIds.has(l.id));

  function handleMergeLeads(primaryId, secondaryIds) {
    const del = new Set((secondaryIds || []).map(String));
    const primary = leads.find(l => String(l.id) === String(primaryId));
    if (!primary) return;
    for (const sid of secondaryIds) {
      if (String(sid) === String(primaryId)) continue;
      activityService.mergeLeadsInto(primaryId, sid);
    }
    setLeads(prev => prev.filter(l => !del.has(String(l.id))));
    setSelectedIds(prev => new Set([...prev].filter(x => !del.has(String(x)))));
    setSelectedLead(prev => {
      if (!prev) return { ...primary };
      if (del.has(String(prev.id))) return { ...primary };
      return prev;
    });
    try {
      window.dispatchEvent(new CustomEvent('crm-activities-changed', { detail: { leadId: '__all__' } }));
    } catch { /* ignore */ }
  }

  function openNextLeadQueue({ excludeLeadIds = [], source = 'session' } = {}) {
    setNextLeadQueue({ excludeLeadIds, source });
  }

  function handleLeadHandoff({ leadId, targetUserId, message, includeSummary = true }) {
    const L = leads.find(l => String(l.id) === String(leadId));
    if (!L || !targetUserId || !message) return;
    const notes = includeSummary ? buildHandoffNotes(message, leadId) : message.trim();
    handleUpdateLead(leadId, { ownerId: targetUserId }, []);
    const due = addWorkdays(new Date(), 1);
    const dueDate = due.toISOString().slice(0, 10);
    const dueDatetime = new Date(due.getFullYear(), due.getMonth(), due.getDate(), 9, 0, 0, 0).toISOString();
    const targetUser = authUsers.find(u => String(u.id) === String(targetUserId));
    void handleSaveTask({
      title: `Übernahme – bitte in 24h anrufen: ${L.name}`,
      notes,
      assignedUserId: targetUserId,
      assignedTo: targetUser?.name || targetUser?.email || 'Kolleg:in',
      lead: { id: L.id, name: L.name, company: L.company },
      leadId: L.id,
      type: 'Anruf',
      taskType: 'Anruf',
      priority: 'Hoch',
      dueDate,
      dueTime: '09:00',
      dueDatetime,
      done: false,
      dismissed: false,
      handoffKind: 'lead_takeover',
      handoffFromUserId: currentUser?.id || null,
      handoffFromName: currentUser?.name || currentUser?.email || 'CRM',
    });
    activityService.addToLead(leadId, {
      id: generateId(),
      type: 'note',
      title: 'Übergabe',
      ts: new Date().toISOString(),
      note: notes,
      by: currentUser?.name || currentUser?.email || 'CRM',
      byUserId: currentUser?.id,
    });
  }

  function handleUpdateLead(id, changes, auditFields = []) {
    // Owner-Lock guard: if lead is locked and user is not owner/admin, block changes (allow unlocking itself)
    if (!Object.prototype.hasOwnProperty.call(changes, 'locked')) {
      const existing = leads.find(l => String(l.id) === String(id));
      if (existing?.locked) {
        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
        const isOwner = existing.ownerId ? String(existing.ownerId) === String(currentUser?.id) : true;
        if (!isAdmin && !isOwner) {
          console.warn('Lead is locked. Only the owner or admin can edit it.');
          return;
        }
      }
    }
    const WON_STATUSES = ['gewonnen', 'closed', 'sold', 'verkauft', 'abgeschlossen', 'kunde'];
    if (changes.status) {
      const newStatus = (changes.status || '').toLowerCase();
      const currentLead = leads.find(l => String(l.id) === String(id));
      const oldStatus = (currentLead?.status || '').toLowerCase();
      if (WON_STATUSES.includes(newStatus) && !WON_STATUSES.includes(oldStatus)) {
        fireConfetti('won');
      }
    }
    setLeads(prev => prev.map(l => {
      if (String(l.id) !== String(id)) return l;
      const next = { ...l, ...changes };
      if (Object.prototype.hasOwnProperty.call(changes, 'status') && changes.status !== l.status) {
        next._statusChangedAt = new Date().toISOString();
      }
      if (auditFields.length > 0) {
        auditFields.forEach(f => {
          if (l[f] !== changes[f]) addAuditEntry(id, f, l[f], changes[f]);
        });
      }
      return next;
    }));
    setSelectedLead(prev => {
      if (!prev || String(prev.id) !== String(id)) return prev;
      const next = { ...prev, ...changes };
      if (Object.prototype.hasOwnProperty.call(changes, 'status') && changes.status !== prev.status) {
        next._statusChangedAt = new Date().toISOString();
      }
      return next;
    });
  }

  /* Confetti on appointment set */
  React.useEffect(() => {
    function onAppt(ev) {
      if (ev?.detail?.outcome === 'reached_appt' || ev?.detail?.outcomeSlug === 'reached_appt') {
        fireConfetti('appt');
      }
    }
    window.addEventListener('crm-appointment-set', onAppt);
    return () => window.removeEventListener('crm-appointment-set', onAppt);
  }, []); // eslint-disable-line

  /* Listen-Tabelle „Letzte Aktivität“ + LeadPanel: `activity` aus Timeline ableiten */
  React.useEffect(() => {
    function syncLeadActivityFromTimeline(ev) {
      const lid = ev?.detail?.leadId;
      const apply = (leadId, nextAct) => {
        if (!nextAct) return;
        const idStr = String(leadId);
        setLeads(prev => prev.map(l => {
          if (String(l.id) !== idStr) return l;
          if (l.activity === nextAct) return l;
          return { ...l, activity: nextAct };
        }));
        setSelectedLead(prev => {
          if (!prev || String(prev.id) !== idStr) return prev;
          if (prev.activity === nextAct) return prev;
          return { ...prev, activity: nextAct };
        });
      };
      if (lid === '__all__') {
        setLeads(prev => prev.map(lead => {
          const nextAct = latestActivityDateDe(activityService.getForLead(lead.id));
          if (!nextAct || lead.activity === nextAct) return lead;
          return { ...lead, activity: nextAct };
        }));
        setSelectedLead(prev => {
          if (!prev) return prev;
          const nextAct = latestActivityDateDe(activityService.getForLead(prev.id));
          if (!nextAct || prev.activity === nextAct) return prev;
          return { ...prev, activity: nextAct };
        });
        return;
      }
      if (lid == null) return;
      const nextAct = latestActivityDateDe(activityService.getForLead(lid));
      apply(lid, nextAct);
    }
    window.addEventListener('crm-activities-changed', syncLeadActivityFromTimeline);
    return () => window.removeEventListener('crm-activities-changed', syncLeadActivityFromTimeline);
  }, []);


  const handleSetActiveSection = useCallback((section) => {
    if (selectedLeadRef.current) setLastLead(selectedLeadRef.current);
    setSelectedLead(null);
    setListVisible(true);
    setActiveSection(section);
  }, []);

  function handleRegionalSession(session) {
    setRegionalSession({ ids: session.leads.map(l => l.id), label: session.label, leads: session.leads });
    setSelectedLead(null);
    setActiveSection('contacts');
  }

  /** Team-CRM: alle Leads sind für alle sichtbar — Zuweisung ist nicht mehr nötig. */
  async function handleAssignLeadsToUser(leadIds, targetUserId) {
    if (!leadIds?.length || !targetUserId) return;
    const targetUser = authUsers.find(u => u.id === targetUserId);
    if (!targetUser) return;

    // Create a unique list id for this assignment batch
    const listId    = `assign_${Date.now()}`;
    const listLabel = `Von ${currentUser?.name || 'Admin'} · ${new Date().toLocaleDateString('de-DE')}`;

    // Stamp ownerId on the selected leads
    setLeads(prev => prev.map(l =>
      leadIds.includes(l.id) ? { ...l, ownerId: targetUserId, list: listId } : l
    ));

    // Add the list to our lists state (admin sees it too)
    if (!lists.some(l => l.id === listId)) {
      setLists(prev => [...prev, { id: listId, label: listLabel }]);
      pinListForUser(listId, currentUser?.id);
      pinListForUser(listId, targetUserId);
    }

    // Send inbox message so the target user gets notified + receives the leads
    const payload = {
      type: 'list_assignment',
      payload: {
        listId,
        listLabel,
        leads: leads.filter(l => leadIds.includes(l.id)),
      },
    };
    try {
      await handleSendMessage(payload, [targetUserId]);
    } catch {}

    setSelectedIds(new Set());
    setNotifications(prev => [...prev, {
      id: generateId(),
      type: 'system',
      title: `${leadIds.length} Lead${leadIds.length > 1 ? 's' : ''} an ${targetUser.name || targetUser.email} zugewiesen`,
      createdAt: new Date().toISOString(),
      read: false,
    }]);
  }

  function handleRemoveListFromUser(listId) {
    if (!listId) return;
    setLists(prev => prev.filter(l => l.id !== listId));
    setLeads(prev => prev.map(l => l.list === listId ? { ...l, list: undefined, ownerId: undefined } : l));
  }

  function userHasList(userId, listId) {
    if (!userId || !listId) return false;
    return leads.some(l => l.list === listId && l.ownerId === userId);
  }

  function handleQuickNoteSave(note) {
    if (!quickNoteLead) return;
    const withTs = { ...note, ts: note.ts || new Date().toISOString() };
    activityService.addToLead(quickNoteLead.id, withTs);
    const act = latestActivityDateDe(activityService.getForLead(quickNoteLead.id));
    handleUpdateLead(quickNoteLead.id, { activity: act });
    setQuickNoteLead(null);
  }

  function handleLeadCommentMentions(payload) {
    const { leadId, leadName, mentions, excerpt, authorName } = payload || {};
    if (!mentions?.length) return;
    mentions.forEach(uid => {
      if (!uid || uid === currentUser?.id) return;
      setNotifications(prev => [{
        id: generateId(),
        type: 'mention',
        title: `${authorName || 'Jemand'} hat dich erwähnt`,
        message: `${leadName || 'Lead'}: ${(excerpt || '').slice(0, 140)}`,
        leadId,
        read: false,
        createdAt: new Date().toISOString(),
      }, ...prev]);
    });
  }

  React.useEffect(() => {
    function onDen() { setTableDensity(getTableDensity()); }
    window.addEventListener('crm-table-density', onDen);
    return () => window.removeEventListener('crm-table-density', onDen);
  }, []);

  React.useEffect(() => {
    const t = setTimeout(() => {
      try { buildOfflineSnapshot(leads); } catch { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [leads]);

  React.useEffect(() => {
    function handleBeforeUnload() {
      try { buildOfflineSnapshot(leads); } catch { /* ignore */ }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [leads]);

  React.useEffect(() => {
    function handleOnline() {
      setReconnectToast(true);
      setTimeout(() => setReconnectToast(false), 3500);
    }
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  React.useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#091e16' : '#f3f7f2');
  }, [theme]);

  function pinListForUser(listId, userId) {
    if (!userId || !listId) return;
    const key = `crm_sidebar_lists_${userId}`;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return; // not configured → all lists visible, nothing to do
      const existing = JSON.parse(raw);
      if (Array.isArray(existing) && !existing.includes(listId)) {
        localStorage.setItem(key, JSON.stringify([...existing, listId]));
      }
    } catch { /* ignore */ }
  }

  function handleImportComplete(listName, importedLeads, errorRows = [], errorValidation = [], targetListId = null) {
    const listId  = targetListId || ('import_' + Date.now());
    const isNew   = !targetListId;
    const newLeads = importedLeads.map(l => ({
      ...l,
      id: l.id || generateId(),
      list: listId,
      ownerId: l.ownerId || currentUser?.id || null,
      importedBy: l.importedBy || currentUser?.id || null,
    }));
    if (isNew) {
      setLists(prev => [...prev, { id: listId, label: listName }]);
      pinListForUser(listId, currentUser?.id);
    }
    setLeads(prev => [...prev, ...newLeads]);
    if (isNew) setImportPopup({ listId, listLabel: listName, count: newLeads.length });
    const existing = importHistoryService.getAll().find(h => h.listId === listId);
    if (existing) {
      importHistoryService.update(existing.id, {
        count: existing.count + newLeads.length,
        importedLeadIds: [...(existing.importedLeadIds || []), ...newLeads.map(l => l.id)],
        errorRows: [...(existing.errorRows || []), ...errorRows],
        errorValidation: [...(existing.errorValidation || []), ...errorValidation],
        errorCount: (existing.errorCount || 0) + errorRows.length,
        updatedAt: new Date().toISOString(),
      });
    } else {
      importHistoryService.add({
        listName, listId, count: newLeads.length,
        userId: currentUser?.id, userName: currentUser?.name || currentUser?.email || 'Admin',
        timestamp: new Date().toISOString(),
        importedLeadIds: newLeads.map(l => l.id),
        errorRows, errorValidation, errorCount: errorRows.length,
      });
    }
  }

  function handleImportUndo(historyId) {
    const entry = importHistoryService.getAll().find(h => String(h.id) === String(historyId));
    if (!entry) return;
    const ids = new Set(entry.importedLeadIds || []);
    setLeads(prev => prev.filter(l => !ids.has(l.id)));
    setLists(prev => prev.filter(l => l.id !== entry.listId));
    importHistoryService.remove(historyId);
  }

  function handleImportErrorLeads(historyId, selectedLeads) {
    const entry = importHistoryService.getAll().find(h => String(h.id) === String(historyId));
    if (!entry || !selectedLeads.length) return;
    const newLeads = selectedLeads.map(l => ({
      ...l,
      id: l.id || generateId(),
      list: entry.listId,
      ownerId: currentUser?.id || null,
      importedBy: currentUser?.id || null,
    }));
    setLeads(prev => [...prev, ...newLeads]);
    const importedSet = new Set(selectedLeads.map((_, i) => i));
    importHistoryService.update(historyId, {
      count: entry.count + newLeads.length,
      importedLeadIds: [...(entry.importedLeadIds || []), ...newLeads.map(l => l.id)],
      errorRows: (entry.errorRows || []).filter((_, i) => !importedSet.has(i)),
      errorValidation: (entry.errorValidation || []).filter((_, i) => !importedSet.has(i)),
      errorCount: (entry.errorCount || 0) - selectedLeads.length,
    });
  }

  function handlePopupOpen() {
    setActiveList(importPopup.listId);
    setActiveSection('contacts');
    setImportPopup(null);
    setLastLead(null);
  }

  async function handleSaveTask(task) {
    try {
      const t = { ...task, id: task.id || generateId(), ownerId: currentUser?.id || null, createdAt: task.createdAt || new Date().toISOString() };
      // Always add to current user's task list so it's immediately visible
      setTasks(prev => {
        const exists = prev.some(x => x.id === t.id);
        return exists ? prev.map(x => x.id === t.id ? t : x) : [...prev, t];
      });
      // If assigned to someone else, also send them an inbox notification
      const targetId = t.assignedUserId;
      if (targetId && targetId !== currentUser?.id) {
        await messageService.send([targetId], {
          type: 'task_assignment',
          from: currentUser?.id,
          fromName: currentUser?.name || currentUser?.email || 'Unbekannt',
          subject: `Aufgabe zugewiesen: ${t.title}`,
          body: t.notes || `${currentUser?.name || 'Unbekannt'} hat dir eine Aufgabe zugewiesen.`,
          status: 'pending',
          payload: { task: t },
        });
      }
    } catch (err) {
      pushError('TASK_SAVE_FAIL', 'Aufgabe konnte nicht gespeichert/zugewiesen werden.', err?.message || String(err));
    }
  }

  async function handleAcceptTask(msg) {
    try {
      const task = { ...msg.payload.task, id: generateId(), createdAt: new Date().toISOString() };
      setTasks(prev => [...prev, task]);
      await messageService.updateStatus(msg.id, 'accepted');
      setInboxMessages(prev => prev.map(m => m.id === msg.id ? { ...m, status: 'accepted', read: true } : m));
    } catch (err) {
      pushError('TASK_ACCEPT_FAIL', 'Aufgabe konnte nicht angenommen werden.', err?.message || String(err));
    }
  }

  async function handleDeclineTask(msg) {
    try {
      await messageService.updateStatus(msg.id, 'declined');
      setInboxMessages(prev => prev.map(m => m.id === msg.id ? { ...m, status: 'declined', read: true } : m));
    } catch (err) {
      pushError('TASK_DECLINE_FAIL', 'Aufgabe konnte nicht abgelehnt werden.', err?.message || String(err));
    }
  }

  const EMAIL_TRIGGER_TYPES = ['Setting', 'Closing terminiert'];

  function handleSaveMeeting(meeting) {
    const m = { ...meeting, id: meeting.id || generateId(), ownerId: currentUser?.id || null, createdAt: meeting.createdAt || new Date().toISOString() };
    setMeetings(prev => [...prev, m]);
    if (EMAIL_TRIGGER_TYPES.includes(meeting.meetingType)) {
      setEmailDraftMeeting(meeting);
    }
  }

  function handleUpdateMeeting(id, changes) {
    setMeetings(prev => prev.map(m => m.id === id ? { ...m, ...changes } : m));
  }

  function handleToggleTask(id) {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));
  }

  async function handleConfirmHandoff(taskId) {
    const task = tasks.find(t => String(t.id) === String(taskId));
    if (!task || task.done || task.handoffKind !== 'lead_takeover') return;
    const leadName = task.lead?.name || 'Lead';
    setTasks(prev => prev.map(t => (
      String(t.id) === String(taskId)
        ? { ...t, done: true, handoffConfirmedAt: new Date().toISOString() }
        : t
    )));
    const fromId = task.handoffFromUserId;
    if (fromId && fromId !== currentUser?.id) {
      try {
        await messageService.send([fromId], {
          type: 'handoff_confirmed',
          from: currentUser?.id,
          fromName: currentUser?.name || currentUser?.email || 'CRM',
          subject: `Übergabe bestätigt: ${leadName}`,
          body: `${currentUser?.name || currentUser?.email || 'Kolleg:in'} hat „${leadName}“ übernommen (Aufgabe erledigt).`,
          status: 'info',
          payload: { taskId, leadId: task.leadId || task.lead?.id },
        });
      } catch (err) {
        pushError('HANDOFF_CONFIRM_FAIL', 'Bestätigung konnte nicht an den Absender gesendet werden.', err?.message || String(err));
      }
    }
  }

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────
  React.useEffect(() => {
    function onKey(e) {
      const tag = document.activeElement?.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;

      if (e.key === 'Escape') {
        if (commandPaletteOpen) {
          e.preventDefault();
          setCommandPaletteOpen(false);
          return;
        }
        if (showInbox) {
          e.preventDefault();
          setShowInbox(false);
          return;
        }
        if (showNewContact) {
          e.preventDefault();
          setShowNewContact(false);
          return;
        }
        if (showSettings) {
          e.preventDefault();
          setShowSettings(false);
          return;
        }
        if (showStatusSettings) {
          e.preventDefault();
          setShowStatusSettings(false);
          return;
        }
        if (selectedLead) {
          e.preventDefault();
          setSelectedLead(null);
          setListVisible(true);
        }
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((prev) => {
          if (prev) return false;
          if (activeSection !== 'contacts' && activeSection !== 'leads') setActiveSection('contacts');
          return true;
        });
        return;
      }

      if (!inInput && e.key === '/') {
        e.preventDefault();
        if (activeSection !== 'contacts' && activeSection !== 'leads') setActiveSection('contacts');
        setCommandPaletteOpen(true);
        return;
      }

      // J/K navigate leads when a lead panel is open
      if (!inInput && (e.key === 'j' || e.key === 'J') && !e.metaKey && !e.ctrlKey && selectedLead) {
        e.preventDefault();
        const idx = leads.findIndex(l => l.id === selectedLead.id);
        if (idx >= 0 && idx < leads.length - 1) setSelectedLead(leads[idx + 1]);
        return;
      }
      if (!inInput && (e.key === 'k' || e.key === 'K') && !e.metaKey && !e.ctrlKey && selectedLead) {
        e.preventDefault();
        const idx = leads.findIndex(l => l.id === selectedLead.id);
        if (idx > 0) setSelectedLead(leads[idx - 1]);
        return;
      }

      if (!inInput && (e.key === 'k' || e.key === 'K') && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        if (activeSection !== 'contacts' && activeSection !== 'leads') setActiveSection('contacts');
        requestAnimationFrame(() => searchInputRef.current?.focus());
        return;
      }

      if (inInput) return;

      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        setMotionPreset(prev => {
          const idx = MOTION_PRESETS.indexOf(prev);
          return MOTION_PRESETS[(idx + 1) % MOTION_PRESETS.length];
        });
        return;
      }
      if (e.key === 'n' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setShowNewContact(true); }
      if (e.key === 't' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setShowCreateTask(true); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedLead, activeSection, commandPaletteOpen, showInbox, showNewContact, showSettings, showStatusSettings]);

  const handleJumpToLead = useCallback((lead) => {
    if (!lead) return;
    setDueNotif(null);
    setActiveSection('contacts');
    const full = leadsRef.current.find(l => l.id === lead.id) || lead;
    setSelectedLead(full);
    setListVisible(false);
    setRecentLeads(prev => {
      const entry = { id: full.id, name: full.name, company: full.company };
      const next = [entry, ...prev.filter(r => r.id !== full.id)].slice(0, 5);
      storage.set('crm_recent', next);
      return next;
    });
  }, []);

  // Check for due tasks every 15 seconds — use refs so interval is created only once
  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const due = tasksRef.current.find(t => !t.done && !t.dismissed && t.dueDatetime && new Date(t.dueDatetime) <= now);
      if (due) {
        setDueNotif(due);
        setTasks(prev => prev.map(t => t.id === due.id ? { ...t, dismissed: true } : t));
      }
    }, 15000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  React.useEffect(() => {
    runReminderNotificationTick({ tasks: tasksRef.current, leads: leadsRef.current });
    const id = setInterval(() => runReminderNotificationTick({ tasks: tasksRef.current, leads: leadsRef.current }), 60 * 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  function handlePopupReturn() {
    setImportPopup(null);
    if (lastLead) {
      setActiveSection('contacts');
      setSelectedLead(lastLead);
      setListVisible(false);
    } else {
      setActiveSection('contacts');
    }
    setLastLead(null);
  }

  const handleSetActiveList = useCallback((id) => {
    if (selectedLeadRef.current) setLastLead(selectedLeadRef.current);
    setSelectedLead(null);
    setListVisible(true);
    setActiveList(id);
    setStatusFilter(null);
    setSelectedIds(new Set());
    setActiveSection('contacts');
  }, []);


  const handleSelectLead = useCallback((lead) => {
    setActiveSection('contacts');
    setSelectedLead(lead);
    if (lead) {
      setListVisible(false);
      setRecentLeads(prev => {
        const entry = { id: lead.id, name: lead.name, company: lead.company };
        const next = [entry, ...prev.filter(r => r.id !== lead.id)].slice(0, 5);
        storage.set('crm_recent', next);
        return next;
      });
    }
  }, []);

  const handleCloseLead = useCallback(() => {
    setSelectedLead(null);
    setListVisible(true);
  }, []);

  React.useEffect(() => {
    const state = {
      section: activeSection,
      list: activeList,
      leadId: selectedLead?.id || null,
    };
    if (restoringHistoryRef.current) {
      restoringHistoryRef.current = false;
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('section', state.section);
    url.searchParams.set('list', state.list);
    if (state.leadId) url.searchParams.set('lead', String(state.leadId));
    else url.searchParams.delete('lead');
    if (!historyReadyRef.current) {
      window.history.replaceState(state, '', `${url.pathname}${url.search}`);
      historyReadyRef.current = true;
    } else {
      window.history.pushState(state, '', `${url.pathname}${url.search}`);
    }
  }, [activeSection, activeList, selectedLead?.id]);

  React.useEffect(() => {
    function onPopState(event) {
      const st = event.state;
      if (!st) return;
      restoringHistoryRef.current = true;
      setActiveSection(st.section || 'dashboard');
      setActiveList(st.list || 'all');
      setStatusFilter(null);
      setSelectedIds(new Set());
      if (st.leadId) {
        const lead = leads.find(l => l.id === st.leadId);
        setSelectedLead(lead || null);
        setListVisible(!lead);
      } else {
        setSelectedLead(null);
        setListVisible(true);
      }
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [leads]);

  function handleChangeDemoBranch(branchKey) {
    const { leads: dl, tasks: dt, meetings: dm } = getDemoData(branchKey);
    const uid = currentUser.id;
    contactService.saveAll(uid, []);
    taskService.saveAll(uid, []);
    meetingService.saveAll(uid, []);
    pipelineService.save(uid, null);
    setLeads(dl);
    setTasks(dt);
    setMeetings(dm);
    setAuthUsers(prev => prev.map(u => u.id === currentUser.id ? { ...u, demoBranch: branchKey } : u));
  }

  function onLogout() {
    handleLogout();
    setShowSettings(false);
  }

  if (!appReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: '#0f8a53', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Wird geladen…</div>
      </div>
    );
  }

  if (!currentUser) {
    return <LoginScreen error={loginError} onLogin={handleLogin} />;
  }

  if (currentUser.mustChangePassword && currentUser.role !== 'demo') {
    return (
      <ForcePasswordChangeScreen
        user={currentUser}
        onComplete={async newPw => {
          // Update password in Supabase Auth so the next login works
          await supabase.auth.updateUser({ password: newPw }).catch(() => {});
          setAuthUsers(prev => prev.map(u =>
            u.id === currentUser.id ? { ...u, password: newPw, mustChangePassword: false, generated: false } : u
          ));
          clearMustChangePassword(currentUser.id);
        }}
      />
    );
  }

  if (currentUser.role === 'demo' && !currentUser.demoBranch) {
    return (
      <DemoBranchScreen
        onComplete={branchKey => {
          const { leads: dl, tasks: dt, meetings: dm } = getDemoData(branchKey);
          setLeads(dl);
          setTasks(dt);
          setMeetings(dm);
          setAuthUsers(prev => prev.map(u =>
            u.id === currentUser.id ? { ...u, demoBranch: branchKey } : u
          ));
        }}
      />
    );
  }

  if (isMobile) {
    return (
      <div data-crm-root data-motion={motionPreset} style={{ height: '100vh', overflow: 'hidden', background: 'transparent', position: 'relative' }}>
        <div className="crm-bg-canvas"><div className="crm-orb crm-orb-1"/><div className="crm-orb crm-orb-2"/><div className="crm-orb crm-orb-3"/></div>
        {/* Eigene Ebene über dem fixierten Mesh-Hintergrund (z=0), sonst: UI „unsichtbar“, Klicks gehen durch pointer-events:none */}
        <div className="crm-mobile-layer">
          <MobileLayout
            leads={leadsForContactsView}
            tasks={tasks}
            meetings={meetings}
            statusMeta={statusMeta}
            authUsers={authUsers}
            lists={listsForContactsView}
            bestandskunden={bestandskunden}
            currentUser={currentUser}
            notifications={notifications}
            auditLog={auditLog}
            statusList={statusList}
            onMergeLeads={handleMergeLeads}
            onUpdateLead={(id, changes, auditFields) => handleUpdateLead(id, changes, auditFields || [])}
            onSaveTask={handleSaveTask}
            onSaveMeeting={handleSaveMeeting}
            onToggleTask={handleToggleTask}
            onCreateContact={handleCreateContact}
            onMergeIntoLead={handleMergeIntoLead}
            onMarkAllNotifsRead={() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))}
            onLogout={onLogout}
            onQuickTask={lead => setQuickTaskLead(lead)}
            onQuickNote={lead => setQuickNoteLead(lead)}
            onQuickMail={lead => setQuickEmailLead(lead)}
            onCarModeComplete={openNextLeadQueue}
            tableDensity={tableDensity}
            theme={theme}
            onToggleTheme={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
          />
          {quickNoteLead && (
            <FloatingNote
              lead={quickNoteLead}
              currentUser={currentUser}
              onSave={handleQuickNoteSave}
              onClose={() => setQuickNoteLead(null)}
            />
          )}
          {quickEmailLead && (
            <FloatingEmail lead={quickEmailLead} onClose={() => setQuickEmailLead(null)} />
          )}
          {(showCreateTask || quickTaskLead) && (
            <FloatingTask
              lead={quickTaskLead || selectedLead}
              currentUser={currentUser}
              users={authUsers}
              onSave={task => { handleSaveTask(task); setShowCreateTask(false); setQuickTaskLead(null); }}
              onClose={() => { setShowCreateTask(false); setQuickTaskLead(null); }}
            />
          )}
        </div>
      </div>
    );
  }

  const forcedDesktopOnPhone = getViewMode() === 'desktop' && detectMobileDevice();

  return (
    <div data-crm-root data-motion={motionPreset} style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'transparent', position: 'relative' }}>
      {/* ── Liquid Glass Background ── */}
      <div className="crm-bg-canvas">
        <div className="crm-orb crm-orb-1" />
        <div className="crm-orb crm-orb-2" />
        <div className="crm-orb crm-orb-3" />
        <div className="crm-orb crm-orb-4" />
      </div>

      {/* Rückweg zur mobilen Ansicht (nur wenn Desktop-Modus am Handy erzwungen wurde) */}
      {forcedDesktopOnPhone && (
        <button onClick={() => setViewMode('auto')}
          style={{ position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', right: 16, zIndex: 900, height: 42, padding: '0 16px', borderRadius: 21, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 6px 20px rgba(15,23,42,0.25)' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
          Mobile Ansicht
        </button>
      )}

      <TopNav activeSection={activeSection} setActiveSection={handleSetActiveSection} search={search} setSearch={v => { setSearch(v); if (v.trim() && activeSection !== 'contacts' && activeSection !== 'leads') handleSetActiveSection('contacts'); }} theme={theme} setTheme={setTheme} onOpenSettings={() => setShowSettings(true)} searchRef={searchInputRef}
        notifications={notifications}
        onMarkNotifRead={id => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))}
        onMarkAllNotifsRead={() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))}
        onDismissNotif={id => setNotifications(prev => prev.filter(n => n.id !== id))}
        onNavigateNotif={leadId => { const l = leads.find(x => x.id === leadId); if (l) handleJumpToLead(l); }}
        leads={leads}
        tasks={tasks}
      />

      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        leads={leads}
        statusList={statusList}
        statusMeta={statusMeta}
        selectedLead={selectedLead}
        activeSection={activeSection}
        onOpenLead={handleJumpToLead}
        onUpdateLead={handleUpdateLead}
        onNewContact={() => { setShowNewContact(true); }}
        onStartActivity={lead => { setQuickTaskLead(lead); }}
        onGoDashboard={() => handleSetActiveSection('dashboard')}
        onGoContacts={() => setActiveSection('contacts')}
      />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Sidebar
          lists={lists}
          onUpdateLists={setLists}
          activeSection={activeSection}
          setActiveSection={handleSetActiveSection}
          activeList={activeList}
          setActiveList={handleSetActiveList}
          counts={counts}
          tasks={tasks}
          onToggleTask={handleToggleTask}
          onOpenLead={handleJumpToLead}
          currentUser={currentUser}
          onLogout={onLogout}
          unreadMessages={inboxMessages.filter(m => !m.read).length}
          onOpenInbox={() => setShowInbox(true)}
          onlineUsers={onlineUsers}
          authUsers={authUsers}
        />

        {showInbox && (
          <InboxPanel
            messages={inboxMessages}
            currentUser={currentUser}
            users={authUsers}
            lists={lists}
            onClose={() => setShowInbox(false)}
            onMarkRead={handleMarkMessageRead}
            onAcceptList={handleAcceptList}
            onDeclineList={handleDeclineList}
            onAcceptTask={handleAcceptTask}
            onDeclineTask={handleDeclineTask}
            onSendMessage={handleSendMessage}
          />
        )}

        <main className="gl-main" style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <Suspense fallback={<div style={{ flex: 1 }} />}>

          {/* ── Linke Spalte (Liste) — slide-out nach links ── */}
          <div style={{
            width: (!selectedLead || listVisible) ? (selectedLead ? '26%' : '100%') : '0px',
            minWidth: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flexShrink: 0,
            willChange: 'width',
            contain: 'layout style',
            transition: 'width var(--motion-panel-width) var(--motion-ease)',
          }}>
            {(activeSection === 'contacts' || activeSection === 'leads') && (
              <ContactsView
                lists={listsForContactsView} leads={leadsForContactsView}
                tasks={tasks}
                activeList={activeList} setActiveList={handleSetActiveList}
                search={search} counts={countsForContactsView}
                selectedIds={selectedIds} setSelectedIds={setSelectedIds}
                selectedLead={selectedLead} setSelectedLead={handleSelectLead}
                setTaskModal={setTaskModal}
                statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                onUpdateLead={handleUpdateLead}
                statusMeta={statusMeta}
                onBulkUpdate={handleBulkUpdate}
                onBulkDelete={handleBulkDelete}
                recentLeads={recentLeads}
                onJumpToRecent={handleSelectLead}
                onQuickTask={lead => setQuickTaskLead(lead)}
                onQuickNote={lead => setQuickNoteLead(lead)}
                onQuickMail={lead => setQuickEmailLead(lead)}
                onCreateTask={() => setShowCreateTask(true)}
                tableDensity={tableDensity}
                onNewContact={() => setShowNewContact(true)}
                onShowStatusSettings={() => setShowStatusSettings(true)}
                onCallSession={sessionLeads => {
                  if (sessionLeads && sessionLeads.length > 0) {
                    setCallSession({ leads: sessionLeads });
                  } else {
                    const saved = loadSavedSessions();
                    if (saved.length > 0) setShowSessionsModal(true);
                    else handleSetActiveSection('calls');
                  }
                }}
                savedSessionCount={loadSavedSessions().length}
                regionalSession={regionalSession}
                onClearRegional={() => setRegionalSession(null)}
                currentUser={currentUser}
                authUsers={authUsers}
                onAssignLeadsToUser={handleAssignLeadsToUser}
                onRemoveListFromUser={handleRemoveListFromUser}
                userHasList={userHasList}
                listAssignVersion={listAssignVersion}
                serviceUserId={serviceUserId}
                onMergeLeads={handleMergeLeads}
              />
            )}
            {activeSection === 'templates' && <TemplatesView />}
            {activeSection === 'import'    && <ImportView onImportComplete={handleImportComplete} existingLeads={leads} onMergeAfterImport={() => setActiveSection('contacts')} onImportUndo={handleImportUndo} onImportErrorLeads={handleImportErrorLeads} lists={lists} />}
            {activeSection === 'calls'     && <CallsView leads={leads} authUsers={authUsers} currentUser={currentUser} onOpenLead={handleJumpToLead} />}
            {activeSection === 'angebote'  && <AngebotsView leads={leads} />}
            {activeSection === 'tasks'     && (
              <TasksView
                tasks={tasks}
                currentUser={currentUser}
                onToggleTask={handleToggleTask}
                onConfirmHandoff={handleConfirmHandoff}
                onOpenLead={handleJumpToLead}
                onCreateTask={() => setShowCreateTask(true)}
              />
            )}
            {activeSection === 'pipeline'  && (
              <PipelineView
                leads={leads}
                onOpenLead={lead => { handleJumpToLead(lead); }}
                pipelineStages={pipelineStages}
                onUpdateStages={setPipelineStages}
                onUpdateLead={handleUpdateLead}
                statusMeta={statusMeta}
                permissions={currentPermissions}
              />
            )}
            {activeSection === 'maps'      && (
              <MapView leads={leads} lists={lists} statusMeta={statusMeta} />
            )}
            {activeSection === 'termin'    && (
              <TerminView
                meetings={meetings}
                onOpenLead={handleJumpToLead}
                onCreateMeeting={() => setShowMeeting(true)}
                onUpdateMeeting={handleUpdateMeeting}
                onCalendarSync={() => setShowCalendarSync(true)}
                outlookConnected={outlookConnected}
                getOutlookCalendarEvents={getOutlookCalendarEvents}
              />
            )}
            {activeSection === 'dashboard' && (
              <DashboardView
                leads={leads}
                meetings={meetings}
                tasks={tasks}
                statusMeta={statusMeta}
                onOpenLead={handleJumpToLead}
                onToggleTask={handleToggleTask}
                currentUser={currentUser}
                authUsers={authUsers}
                permissions={currentPermissions}
                onOpenAnalytics={() => handleSetActiveSection('analytics')}
                onStartNextLead={() => openNextLeadQueue({ source: 'dashboard' })}
              />
            )}
            {activeSection === 'companies' && (
              <CompanyView
                leads={leads}
                statusMeta={statusMeta}
                onOpenLead={handleJumpToLead}
              />
            )}
            {activeSection === 'reports'   && (
              <ReportsView
                leads={leads}
                tasks={tasks}
                meetings={meetings}
                statusMeta={statusMeta}
                pipelineStages={pipelineStages}
                authUsers={authUsers}
                currentUser={currentUser}
                permissions={currentPermissions}
              />
            )}

            {activeSection === 'automations' && (
              <AutomationView
                rules={automations}
                onSave={r => setAutomations(prev => { const idx = prev.findIndex(x => x.id === r.id); return idx >= 0 ? prev.map(x => x.id === r.id ? r : x) : [...prev, r]; })}
                onDelete={id => setAutomations(prev => prev.filter(r => r.id !== id))}
                onToggle={id => setAutomations(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r))}
              />
            )}
            {activeSection === 'analytics' && (
              <AnalyticsView leads={leads} tasks={tasks} meetings={meetings} statusMeta={statusMeta} authUsers={authUsers} currentUser={currentUser} onOpenLead={handleJumpToLead} />
            )}
            {activeSection === 'insights' && (
              <InsightsView leads={leads} meetings={meetings} auditLog={auditLog} statusList={statusList} authUsers={authUsers} currentUser={currentUser} />
            )}
            {activeSection === 'trash' && (
              <TrashView
                trash={trash}
                onRestore={handleRestoreFromTrash}
                onDelete={handlePermanentDelete}
              />
            )}
          </div>

          {/* ── Rechts: Lead Panel volle Höhe ── */}
          {selectedLead && (
            <div
              key={selectedLead.id}
              style={{
                flex: 1,
                minWidth: 0,
                width: 0,
                overflow: 'hidden',
                display: 'flex',
                position: 'relative',
                borderLeft: '1.5px solid rgba(29,190,116,0.18)',
                animation: 'glPanelIn var(--motion-panel-enter) var(--motion-ease) both',
                willChange: 'transform, opacity',
              }}
            >
              {/* Toggle-Button: Liste ein-/ausblenden */}
              <button
                onClick={() => setListVisible(v => !v)}
                title={listVisible ? 'Liste ausblenden' : 'Liste einblenden'}
                style={{
                  position: 'absolute',
                  left: -14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 14,
                  height: 64,
                  borderRadius: '6px 0 0 6px',
                  background: 'white',
                  border: '1.5px solid rgba(29,190,116,0.28)',
                  borderRight: 'none',
                  boxShadow: '-2px 0 10px rgba(29,190,116,0.12)',
                  cursor: 'pointer',
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background var(--motion-fade-fast)',
                  padding: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#effaf4'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" stroke="rgba(29,190,116,0.7)" strokeWidth="1.8" strokeLinecap="round">
                  {listVisible
                    ? <><polyline points="6 2 2 7 6 12"/></>
                    : <><polyline points="2 2 6 7 2 12"/></>
                  }
                </svg>
              </button>

              <LeadPanel lead={selectedLead} onClose={handleCloseLead}
                onUpdateLead={(id, changes, auditFields) => handleUpdateLead(id, changes, auditFields || [])}
                onDeleteLead={handleDeleteLead}
                onSaveTask={handleSaveTask}
                statusMeta={statusMeta} lists={lists} bestandskunden={bestandskunden}
                auditLog={auditLog.filter(e => e.leadId === selectedLead?.id)}
                leads={leads}
                onRegionalSession={handleRegionalSession}
                currentUser={currentUser}
                authUsers={authUsers}
                onLeadHandoff={handleLeadHandoff}
                userScopeId={serviceUserId}
                onCommentMentions={handleLeadCommentMentions}
              />
            </div>
          )}

        </Suspense>
        </main>
      </div>

      {taskModal && (
        <TaskModal
          leads={selectedLeads}
          currentUser={currentUser}
          onClose={() => setTaskModal(false)}
          onConfirm={(payload) => {
            const leadsToAssign = selectedLeads.length ? selectedLeads : [];
            leadsToAssign.forEach(lead => {
              handleSaveTask({
                id: generateId(),
                title: payload.note
                  ? `${payload.type}: ${lead.name}`
                  : `${payload.type} – ${lead.name}`,
                type: payload.type,
                taskType: payload.type,
                priority: 'Keine',
                dueDate: payload.dueDate,
                dueTime: payload.dueTime,
                dueDatetime: payload.dueDatetime,
                notes: payload.note,
                assignedTo: payload.assignedTo,
                assignedUserId: payload.assignedUserId,
                lead: { id: lead.id, name: lead.name, company: lead.company },
                done: false,
                dismissed: false,
              });
            });
            setTaskModal(false);
            setSelectedIds(new Set());
          }}
        />
      )}

      {/* ── Due-task notification toast ── */}
      {dueNotif && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 700,
          background: 'var(--bg2)', borderRadius: 14,
          border: '1px solid var(--border)',
          boxShadow: '0 8px 32px rgba(15,23,42,0.18)',
          padding: '14px 18px', width: 340,
          display: 'flex', gap: 12, alignItems: 'flex-start',
          animation: 'slideInRight .25s ease',
        }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(29,190,116,0.12)', border: '1.5px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>Aufgabe fällig</div>
            <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dueNotif.title}</div>
            {dueNotif.lead && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{dueNotif.lead.name} · {dueNotif.lead.company}</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {dueNotif.lead && (
                <button onClick={() => handleJumpToLead(dueNotif.lead)} style={{
                  height: 28, padding: '0 12px', borderRadius: 7,
                  background: 'var(--accent)', border: 'none', color: '#fff',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>Zum Lead</button>
              )}
              <button onClick={() => setDueNotif(null)} style={{
                height: 28, padding: '0 12px', borderRadius: 7,
                background: 'var(--bg4)', border: '1px solid var(--border)',
                color: 'var(--text2)', fontSize: 12, cursor: 'pointer',
              }}>Schließen</button>
            </div>
          </div>
          <button onClick={() => setDueNotif(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', flexShrink: 0, padding: 2 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* ── Reconnect toast ── */}
      {reconnectToast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9100, background: '#16a34a', color: 'white',
          borderRadius: 10, padding: '10px 20px',
          fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 16px rgba(22,163,74,0.35)',
          animation: 'slideInRight .2s ease',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          Verbindung wiederhergestellt
        </div>
      )}

      {/* ── Status Settings ── */}
      {showStatusSettings && (
        <StatusSettingsModal
          statusList={statusList}
          onSave={handleSaveStatusList}
          onClose={() => setShowStatusSettings(false)}
        />
      )}

      {/* ── New Contact ── */}
      {showNewContact && (
        <NewContactModal
          onClose={() => setShowNewContact(false)}
          onCreate={handleCreateContact}
          onMergeIntoLead={handleMergeIntoLead}
          onOpenLead={handleJumpToLead}
          existingLeads={leads}
        />
      )}

      {/* ── Settings ── */}
      {showSettings && (
        <SettingsModal
          theme={theme}
          setTheme={setTheme}
          motionPreset={motionPreset}
          onMotionPresetChange={setMotionPreset}
          currentUser={currentUser}
          users={authUsers}
          onUsersChange={handleUsersChange}
          onLogout={onLogout}
          onOpenBranding={() => { setShowSettings(false); setShowBranding(true); }}
          onClose={() => { setShowSettings(false); setBestandskunden(loadBestandskunden()); }}
          onChangeDemoBranch={currentUser.role === 'demo' ? handleChangeDemoBranch : undefined}
          lists={lists}
          leads={leads}
          onDeleteList={handleRemoveListFromUser}
          auditLog={auditLog}
          trash={trash}
          onRestoreFromTrash={handleRestoreFromTrash}
          onEmptyTrash={() => setTrash([])}
          statusList={statusList}
          onSaveStatusList={handleSaveStatusList}
        />
      )}
      {showBranding && <BrandingModal onClose={() => setShowBranding(false)} />}

      {quickNoteLead && (
        <FloatingNote
          lead={quickNoteLead}
          currentUser={currentUser}
          onSave={handleQuickNoteSave}
          onClose={() => setQuickNoteLead(null)}
        />
      )}
      {quickEmailLead && (
        <FloatingEmail lead={quickEmailLead} onClose={() => setQuickEmailLead(null)} />
      )}

      {/* ── Call Session ── */}
      {callSession && (
        <CallSessionOverlay
          leads={callSession.leads}
          sessionId={callSession.sessionId}
          startIndex={callSession.startIndex || 0}
          initDone={callSession.initDone || []}
          onClose={() => { setCallSession(null); setSelectedIds(new Set()); }}
          onSessionComplete={openNextLeadQueue}
          onUpdateLead={handleUpdateLead}
          onSaveTask={handleSaveTask}
          statusMeta={statusMeta}
          lists={lists}
          bestandskunden={bestandskunden}
        />
      )}

      {/* ── Sessions Modal ── */}
      {showSessionsModal && (
        <CallSessionsModal
          allLeads={leads}
          onClose={() => setShowSessionsModal(false)}
          onResume={(session, resumeLeads) => {
            setCallSession({
              leads: resumeLeads,
              sessionId: session.id,
              startIndex: session.index || 0,
              initDone: session.done || [],
            });
            setShowSessionsModal(false);
          }}
          onNew={() => setShowSessionsModal(false)}
        />
      )}

      {/* ── Create Task ── */}
      {(showCreateTask || quickTaskLead) && (
        <FloatingTask
          lead={quickTaskLead || selectedLead}
          currentUser={currentUser}
          users={authUsers}
          onSave={task => { handleSaveTask(task); setShowCreateTask(false); setQuickTaskLead(null); }}
          onClose={() => { setShowCreateTask(false); setQuickTaskLead(null); }}
        />
      )}

      {/* ── Floating Meeting ── */}
      {showMeeting && (
        <FloatingMeeting
          lead={selectedLead}
          meetings={meetings}
          currentUser={currentUser}
          onSave={handleSaveMeeting}
          onClose={() => setShowMeeting(false)}
        />
      )}

      {/* ── Import success popup ── */}
      {importPopup && (
        <div
          onClick={e => { if (e.target === e.currentTarget) handlePopupReturn(); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.38)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 18, padding: '32px 36px', width: 420, boxShadow: '0 24px 56px rgba(15,23,42,0.22)', border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(22,163,74,0.12)', border: '2px solid rgba(22,163,74,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2"><path d="M20 6L9 17l-5-5"/></svg>
            </div>

            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Import abgeschlossen</div>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 999, padding: '4px 14px', marginBottom: 10 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{importPopup.listLabel}</span>
            </div>

            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 22 }}>
              <strong style={{ color: 'var(--text)' }}>{importPopup.count}</strong> Kontakt{importPopup.count !== 1 ? 'e' : ''} wurden erfolgreich importiert.
              <br/>Möchten Sie die neue Liste sofort öffnen?
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handlePopupReturn} style={{
                flex: 1, height: 40, borderRadius: 10,
                background: 'var(--bg4)', border: '1px solid var(--border)',
                color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
              }}>
                {lastLead ? `← ${lastLead.name}` : 'Schließen'}
              </button>
              <button onClick={handlePopupOpen} style={{
                flex: 2, height: 40, borderRadius: 10,
                background: 'linear-gradient(135deg,#0f8a53,#159c5f)',
                border: 'none', color: 'white', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', boxShadow: '0 4px 12px rgba(29,190,116,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round"/></svg>
                Liste öffnen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Calendar Sync ── */}
      {showCalendarSync && (
        <CalendarSyncModal
          meetings={meetings}
          onClose={() => setShowCalendarSync(false)}
        />
      )}

      {/* ── Appointment Email Draft ── */}
      {emailDraftMeeting && (
        <AppointmentEmailModal
          meeting={emailDraftMeeting}
          outlookConnected={outlookConnected}
          onClose={() => setEmailDraftMeeting(null)}
        />
      )}

      <FloatingTeamFeed
        currentUser={currentUser}
        leads={leads}
        onOpenLead={handleJumpToLead}
      />

      {nextLeadQueue && (
        <NextLeadQueueModal
          leads={leads}
          tasks={tasks}
          excludeLeadIds={nextLeadQueue.excludeLeadIds}
          source={nextLeadQueue.source}
          onClose={() => setNextLeadQueue(null)}
          onOpenLead={lead => {
            setNextLeadQueue(null);
            handleJumpToLead(lead);
          }}
        />
      )}

      <SyncStatusBar />

      {/* ── Outlook auth toast ── */}
      {outlookAuthMsg && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 900,
          background: outlookAuthMsg.startsWith('✓') ? '#16a34a' : '#dc2626',
          color: '#fff', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600,
          boxShadow: '0 6px 24px rgba(0,0,0,0.22)', animation: 'slideInRight .25s ease',
          whiteSpace: 'nowrap',
        }}>
          {outlookAuthMsg}
        </div>
      )}

      {/* ── App error toasts ── */}
      {appErrors.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380,
        }}>
          {appErrors.map(err => (
            <div key={err.id} style={{
              background: '#1e1e1e', border: '1.5px solid #dc2626',
              borderRadius: 12, padding: '12px 14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              animation: 'slideInRight .2s ease',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Fehler · {err.code}
                  </span>
                </div>
                <button
                  onClick={() => setAppErrors(prev => prev.filter(e => e.id !== err.id))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c7a70', padding: 2, flexShrink: 0 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ fontSize: 13, color: '#f3f4f6', marginTop: 6, lineHeight: 1.45 }}>{err.message}</div>
              {err.detail && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {err.detail}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  onClick={() => {
                    const text = `Fehler-Code: ${err.code}\nZeit: ${err.ts}\nMeldung: ${err.message}\nDetail: ${err.detail || '–'}`;
                    navigator.clipboard?.writeText(text);
                  }}
                  style={{
                    height: 26, padding: '0 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.35)',
                    color: '#fca5a5', cursor: 'pointer',
                  }}
                >
                  Kopieren
                </button>
                <span style={{ fontSize: 10, color: '#6c7a70', alignSelf: 'center' }}>{err.ts}</span>
              </div>
            </div>
          ))}
        </div>
      )}


      {/* ── Confetti Overlay ── */}
      {confetti && <ConfettiOverlay reason={confetti.reason} />}

    </div>
  );
}

function ConfettiOverlay({ reason }) {
  const isWon = reason === 'won';
  const label = isWon ? '🎉 Abschluss!' : '📅 Termin gesetzt!';
  const colors = isWon
    ? ['#f0b429','#1DBE74','#B4EF4A','#34d399','#fbbf24','#86efac','#0E3D2E']
    : ['#1DBE74','#B4EF4A','#34d399','#4ade80','#d7f3a0','#86efac'];

  React.useEffect(() => {
    playKaching(isWon);
  }, []); // eslint-disable-line

  const particles = React.useMemo(() => {
    return Array.from({ length: 80 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      x: 10 + Math.random() * 80,
      delay: Math.random() * 0.8,
      duration: 2.5 + Math.random() * 1.5,
      size: 6 + Math.random() * 8,
      shape: Math.random() > 0.5 ? 'circle' : 'rect',
      rotSpeed: (Math.random() - 0.5) * 720,
    }));
  }, []); // eslint-disable-line

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(var(--rot)); opacity: 0; }
        }
        @keyframes confettiLabel {
          0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          15%  { transform: translate(-50%, -50%) scale(1.1); opacity: 1; }
          25%  { transform: translate(-50%, -50%) scale(1); }
          80%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -60%) scale(0.9); }
        }
      `}</style>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: -12,
          width: p.size,
          height: p.shape === 'circle' ? p.size : p.size * 0.5,
          borderRadius: p.shape === 'circle' ? '50%' : 2,
          background: p.color,
          '--rot': `${p.rotSpeed}deg`,
          animation: `confettiFall ${p.duration}s ${p.delay}s cubic-bezier(0.3,0,0.7,1) forwards`,
        }} />
      ))}
      <div style={{
        position: 'absolute', top: '38%', left: '50%',
        fontSize: 36, fontWeight: 900, color: '#fff',
        textShadow: '0 2px 16px rgba(0,0,0,0.35), 0 0 40px rgba(255,255,255,0.5)',
        animation: 'confettiLabel 3.2s ease forwards',
        whiteSpace: 'nowrap', letterSpacing: '-0.01em',
      }}>{label}</div>
    </div>
  );
}

function playKaching(isWon) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const t = ctx.currentTime;
    if (isWon) {
      [523, 659, 784, 1047].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        const start = t + i * 0.12;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.18, start + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.45);
        osc.start(start); osc.stop(start + 0.5);
      });
    } else {
      [659, 784].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        const start = t + i * 0.14;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.16, start + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4);
        osc.start(start); osc.stop(start + 0.5);
      });
    }
  } catch { /* audio not available */ }
}
